/**
 * All logic behind the Form Flow editor: loading a flow, seeding a brand-new one from its
 * template, every level / step / action mutation, the saveable-state rule, and both save paths
 * (update an existing flow, or save the in-flight flow as a new reusable one).
 *
 * Platform-agnostic on purpose: written to be consumed by `packages/mobile` as well as
 * `packages/web`, so it returns state, handlers and option lists only — never markup.
 */
import { useTranslation, untranslated } from "@i18n";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import { formFlowController, getTemplates } from "@Actions/Forms";
import { showToastNotification } from "@Reducers/toastSlice";
import { SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import type { RootState } from "@Reducers/index";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import {
  buildAddUserQuery,
  collectUsedSectionIds,
  createFlowStep,
  createIntermediateLevel,
  DEFAULT_ACTION_BUTTON_TEXTS,
  FORM_FLOW_MENU_ACTIONS,
  FORM_FLOW_STEP_TYPES,
  LEVEL_HEADING_MAX_LENGTH,
  getAddActionMenuItems,
  getAddStepMenuItems,
  getActionMenuItems,
  getFormFlowActionSteps,
  getInitialFormFlowLevels,
  getLevelMenuItems,
  getSectionsInUseForStep,
  getSelectableSections,
  getStepMenuItems,
  isFormFlowSaveable,
  stripLevelEditFlags,
  toFlowSection,
  toFlowUserFromFullName,
  toPersistableFormFlow,
} from "./formFlow.constants";
import type {
  FormFlowActionStep,
  FormFlowAddUserQuery,
  FormFlowLevel,
  FormFlowStep,
  FormFlowUser,
} from "./formFlow.types";

interface UseFormFlowCreationParams {
  /** Existing flow to load and update. `null` seeds a new flow from `selectedTemplate`. */
  formFlowId?: string | null;
  projectId?: string;
  /** Space the flow belongs to. Falls back to the loaded flow's own space. */
  spaceId?: string;
  /** Template a brand-new flow is seeded from. */
  selectedTemplate?: any;
  /** Which screen embeds the editor — see `FORM_FLOW_SOURCES`. */
  source?: string;
  /** Edit a single level in isolation and report it back through `onLevelChange`. */
  singleLevelEditMode?: boolean;
  /** Levels to seed from in single-level edit mode. */
  formFlow?: FormFlowLevel[] | null;
  /** Index of the level being edited in single-level edit mode. */
  editLevelIndex?: number | null;
  /** Called with the edited level (edit flags stripped) and whether it is saveable. */
  onLevelChange?: (level: FormFlowLevel, isSaveable: boolean) => void;
  /** Leaves the editor — also called after a successful save. */
  handleBackClick?: () => void;
}

function useFormFlowCreation({
  formFlowId = null,
  projectId,
  spaceId,
  selectedTemplate = {},
  source = "",
  singleLevelEditMode = false,
  formFlow = null,
  editLevelIndex = null,
  onLevelChange = () => {},
  handleBackClick = () => {},
}: UseFormFlowCreationParams) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const spaceUsers = useSelector((state: RootState) => state.spaceUsers);

  const [formFlowLevels, setFormFlowLevels] = useState<any>(() =>
    singleLevelEditMode && Array.isArray(formFlow)
      ? formFlow.map((level: any) => ({
          ...level,
          headingEditMode: false,
          subBtnEditMode: false,
          rejectBtnEditMode: false,
          approveBtnEditMode: false,
          rollBackBtnEditMode: false,
        }))
      : [],
  );
  const [formFlowData, setFormFlowData] = useState<any>({});
  const [updatedTemplate, setUpdatedTemplate] = useState<any>(selectedTemplate);
  const [editMode, setEditMode] = useState(!!singleLevelEditMode);
  const [modalState, setModalState] = useState<any>({
    modalName: "",
    levelIndex: null,
    stepIndex: null,
    currentUsers: [],
    currentSections: [],
  });
  const [allSelectedSections, setAllSelectedSections] = useState<any>([]);
  const [openNewFlowSaveModal, setOpenNewFlowSaveModal] = useState({ status: false, title: "" });
  const [addUserQuery, setAddUserQuery] = useState<FormFlowAddUserQuery | null>(null);

  /** Explicit space wins; otherwise the space of the loaded flow. */
  const resolvedSpaceId = spaceId || formFlowData?.space?._id;

  useEffect(() => {
    if (resolvedSpaceId) {
      setAddUserQuery(buildAddUserQuery(resolvedSpaceId));
    }
  }, [resolvedSpaceId]);

  /** The four level actions, translated. */
  const actionSteps = getFormFlowActionSteps();

  /* ---------------- loading ---------------- */

  const fetchFormFlowData = useCallback(async () => {
    // Only ever called with a flow selected — the caller guards on `formFlowId`.
    const res = (await formFlowController({ formFlowId: formFlowId as ObjectIdString })) as any;
    if (res.data.status) {
      const loadedFlow = res.data.result.formFlow.map((level: any) => ({
        ...level,
        rejectBtnEditMode: false,
        approveBtnEditMode: false,
        rollBackBtnEditMode: false,
        headingEditMode: false,
        subBtnEditMode: false,
      }));
      setFormFlowData(res.data.result);
      setFormFlowLevels(loadedFlow);
    }
  }, [formFlowId]);

  /** Template the loaded flow is built on. */
  const selectedFormId = formFlowData.selectedForm?._id;

  const getUpdatedTemplate = useCallback(async () => {
    if (!selectedFormId) return;
    const data = await getTemplates({
      formId: selectedFormId,
      isArchived: false,
    });
    if (data.data.status) {
      setUpdatedTemplate({ ...data.data.result });
    }
  }, [selectedFormId]);

  // Fetch the template once the loaded flow names one. Re-runs once it arrives (`updatedTemplate._id`
  // becomes set), which the guard then turns into a no-op — so this still fetches exactly once.
  useEffect(() => {
    if (!updatedTemplate._id && selectedFormId) {
      getUpdatedTemplate();
    }
  }, [updatedTemplate._id, selectedFormId, getUpdatedTemplate]);

  useEffect(() => {
    if (formFlowId) {
      fetchFormFlowData();
    }
  }, [formFlowId, fetchFormFlowData]);

  /**
   * Seeds the two default levels for a new flow: every template section is fillable in
   * Pre-Submission and verifiable in Post-submission, and every space user may act at both.
   */
  const setInitialData = () => {
    const usersData: FormFlowUser[] = spaceUsers.map((user: any) => toFlowUserFromFullName(user));
    const templateSections = updatedTemplate.template.map((section: any) => toFlowSection(section));

    const [preSubmissionLevel, postSubmissionLevel] = getInitialFormFlowLevels();

    preSubmissionLevel.dataSource = [
      { type: FORM_FLOW_STEP_TYPES.FILL, sections: [...templateSections], users: [...usersData] },
    ];
    preSubmissionLevel.allowedUsersToSubmit = [...usersData];
    preSubmissionLevel.allowedUsersToReject = [...usersData];

    postSubmissionLevel.dataSource = [
      { type: FORM_FLOW_STEP_TYPES.VERIFY, sections: [...templateSections], users: [...usersData] },
    ];
    postSubmissionLevel.allowedUsersToReject = [...usersData];
    postSubmissionLevel.allowedUsersToApprove = [...usersData];
    postSubmissionLevel.allowedUsersToRollBack = [...usersData];

    setFormFlowLevels([preSubmissionLevel, postSubmissionLevel]);
  };

  // `setInitialData` closes over `spaceUsers`, which loads asynchronously and can be refetched at any
  // time. Seeding must happen only when a template first becomes available: re-seeding on a
  // space-users change would throw away whatever the user had already built. So the effect triggers
  // on the template and reaches the current seeder through a ref.
  const setInitialDataRef = useRef(setInitialData);
  setInitialDataRef.current = setInitialData;

  useEffect(() => {
    // In single-level edit mode `formFlowLevels` is seeded from the `formFlow` param, so skip
    // generating a fresh default flow (which would overwrite the level being edited).
    if (!formFlowId && updatedTemplate._id && !singleLevelEditMode) {
      setInitialDataRef.current();
    }
  }, [formFlowId, updatedTemplate._id, singleLevelEditMode]);

  /* ---------------- derived state ---------------- */

  // Which sections a Fill step and a Verify step cover, across every level. Derived rather than
  // mirrored into state so they can never lag a render behind `formFlowLevels`.
  const { fill: allSectionsUsedForFill, verify: allSectionsUsedForVerify } = useMemo(
    () => collectUsedSectionIds(formFlowLevels),
    [formFlowLevels],
  );

  /** Whether the flow may be saved: full Fill+Verify coverage, and every step and action manned. */
  const saveBtnState = useMemo(
    () =>
      isFormFlowSaveable({
        levels: formFlowLevels,
        templateSectionCount: updatedTemplate?.template?.length,
        sectionsUsedForFill: allSectionsUsedForFill,
        sectionsUsedForVerify: allSectionsUsedForVerify,
      }),
    [formFlowLevels, updatedTemplate, allSectionsUsedForFill, allSectionsUsedForVerify],
  );

  /** Section ids the add-sections picker must hide for this step. */
  const getSectionsInUse = (step: FormFlowStep) =>
    getSectionsInUseForStep({
      step,
      sectionsUsedForFill: allSectionsUsedForFill,
      sectionsUsedForVerify: allSectionsUsedForVerify,
    });

  /** Sections the add-sections picker may offer for the step the modal was opened for. */
  const selectableSections = getSelectableSections({
    template: updatedTemplate?.template ?? [],
    currentSections: modalState.currentSections,
    stepType: modalState?.stepData?.type,
    sectionsUsedForFill: allSectionsUsedForFill,
  });

  /* ---------------- modals ---------------- */

  const closeAddModal = () => {
    setModalState({
      modalName: "",
      levelIndex: null,
      stepIndex: null,
      currentUsers: [],
      currentSections: [],
    });
  };

  /** Opens the add-sections picker for one step. */
  const openSectionsModal = ({ stepData, levelIndex, stepIndex }: any) => {
    setModalState({
      modalName: "sections",
      levelIndex,
      stepIndex,
      currentUsers: [],
      currentSections: getSectionsInUse(stepData),
      stepData,
    });
  };

  /** Opens the add-users picker for one step. */
  const openStepUsersModal = ({ stepData, levelIndex, stepIndex }: any) => {
    setModalState({
      modalName: "users",
      levelIndex,
      stepIndex,
      currentUsers: stepData.users,
      currentSections: [],
    });
  };

  /** Opens the add-users picker for one level action. */
  const openActionUsersModal = ({ level, levelIndex, actionStep }: any) => {
    setModalState({
      modalName: "users",
      levelIndex,
      actionStep,
      currentUsers: level[actionStep.usersField],
      currentSections: [],
    });
  };

  /* ---------------- mutations ---------------- */

  /** Writes the picked users onto the step (or the action) the modal was opened for. */
  const handleAddUsersClick = ({ allSelectedUsers }: any) => {
    const allUsers: FormFlowUser[] = allSelectedUsers.map((user: { id: string; fullName: string }) => ({
      id: user.id,
      name: user.fullName,
    }));
    const modifiedData: any = _.cloneDeep(formFlowLevels);
    if (modalState.actionStep) {
      modifiedData[modalState.levelIndex][modalState.actionStep.usersField] = allUsers;
    } else {
      modifiedData[modalState.levelIndex].dataSource[modalState.stepIndex].users = allUsers;
    }
    setFormFlowLevels(modifiedData);
  };

  /** Toggles one section in the add-sections picker. */
  const handleSectionSelection = ({ isChecked, sectionId }: any) => {
    if (isChecked) {
      setAllSelectedSections((prev: any) => [...prev, sectionId]);
    } else {
      setAllSelectedSections((prev: any) => prev.filter((secId: any) => secId != sectionId));
    }
  };

  /** Appends the picked sections to the step the modal was opened for. */
  const handleAddSectionsClick = () => {
    const modifiedData = _.cloneDeep(formFlowLevels);
    const sectionToAdd = updatedTemplate.template
      .filter((section: any) => allSelectedSections.includes(section._id))
      .map((section: any) => toFlowSection(section));
    modifiedData[modalState.levelIndex].dataSource[modalState.stepIndex].sections = [
      ...modifiedData[modalState.levelIndex].dataSource[modalState.stepIndex].sections,
      ...sectionToAdd,
    ];
    setFormFlowLevels(modifiedData);
    setAllSelectedSections([]);
    closeAddModal();
  };

  /** Commits a renamed action button. */
  const saveNewBtnName = ({ args, actionStep, levelIndex }: any) => {
    const modifiedData = _.cloneDeep(formFlowLevels);
    modifiedData[levelIndex][actionStep.btnTxtEditMode] = false;
    modifiedData[levelIndex][actionStep.btnTxtField] = args?.value ?? "";
    setFormFlowLevels(modifiedData);
  };

  const cancelBtnNameChange = ({ actionStep, levelIndex }: any) => {
    const modifiedData = _.cloneDeep(formFlowLevels);
    modifiedData[levelIndex][actionStep.btnTxtEditMode] = false;
    setFormFlowLevels(modifiedData);
  };

  /** Puts one action button caption into in-place edit mode. */
  const handleActionBtnEdit = ({ levelIndex, actionStep }: any) => {
    const modifiedData = _.cloneDeep(formFlowLevels);
    modifiedData[levelIndex][actionStep.btnTxtEditMode] = true;
    setFormFlowLevels(modifiedData);
  };

  const handleEditHeaderCancelClick = (levelIndex: any) => {
    const data = _.cloneDeep(formFlowLevels);
    data[levelIndex] = { ...data[levelIndex], headingEditMode: false };
    setFormFlowLevels(() => data);
  };

  /** Commits a renamed level heading (capped at `LEVEL_HEADING_MAX_LENGTH`). */
  const saveNewHeaderName = ({ args, levelIndex }: any) => {
    const data = _.cloneDeep(formFlowLevels);
    data[levelIndex] = {
      ...data[levelIndex],
      heading: String(args?.value ?? "").substring(0, LEVEL_HEADING_MAX_LENGTH),
      headingEditMode: false,
    };
    setFormFlowLevels(() => data);
  };

  /** Inserts a new intermediate level. Defaults to just before the final level. */
  const handleAddLevelClick = (destinationIndex = formFlowLevels.length - 1) => {
    const currentLevels = _.cloneDeep(formFlowLevels);
    const newLevel = createIntermediateLevel(formFlowLevels.length);
    setFormFlowLevels(() => [
      ...currentLevels.slice(0, destinationIndex),
      newLevel,
      ...currentLevels.slice(destinationIndex),
    ]);
  };

  /** Toggles a level condition such as `allowDynamicUserConfig`. */
  const handleConditionChange = ({ levelIndex, field, value }: any) => {
    const modifiedData = _.cloneDeep(formFlowLevels);
    modifiedData[levelIndex][field] = value;
    setFormFlowLevels(modifiedData);
  };

  /**
   * Every level / step / action mutation driven by a menu pick. `action` may be passed
   * explicitly (mobile, and the inline remove buttons) or read from a web menu event's
   * `args.item.id`.
   */
  const handleOptionSelect = ({ args, action, levelIndex, stepIndex, actionStep }: any) => {
    if (!action) {
      action = args.item.id;
    }
    let modifiedData = _.cloneDeep(formFlowLevels);

    switch (action) {
      case FORM_FLOW_MENU_ACTIONS.DELETE_LEVEL: {
        modifiedData = modifiedData.filter((_level: any, index: number) => index != levelIndex);
        setFormFlowLevels(modifiedData);
        break;
      }
      case FORM_FLOW_MENU_ACTIONS.DELETE_STEP: {
        modifiedData[levelIndex].dataSource = modifiedData[levelIndex].dataSource.filter(
          (_step: any, index: number) => index != stepIndex,
        );
        setFormFlowLevels(modifiedData);
        break;
      }
      case FORM_FLOW_MENU_ACTIONS.DELETE_ACTION: {
        delete modifiedData[levelIndex][actionStep.usersField];
        delete modifiedData[levelIndex][actionStep.btnTxtEditMode];
        delete modifiedData[levelIndex][actionStep.btnTxtField];
        setFormFlowLevels(modifiedData);
        break;
      }
      case FORM_FLOW_MENU_ACTIONS.ADD_LEVEL_AFTER: {
        handleAddLevelClick(levelIndex + 1);
        break;
      }
      case FORM_FLOW_MENU_ACTIONS.ADD_LEVEL_BEFORE: {
        handleAddLevelClick(levelIndex);
        break;
      }
      case FORM_FLOW_MENU_ACTIONS.RENAME_LEVEL: {
        modifiedData[levelIndex] = {
          ...modifiedData[levelIndex],
          headingEditMode: true,
        };
        setFormFlowLevels(modifiedData);
        break;
      }
      case FORM_FLOW_MENU_ACTIONS.DUPLICATE_LEVEL: {
        // A duplicated level keeps its steps and users but starts with no sections: a section
        // may only be filled/verified at one level.
        const levelToAdd = _.cloneDeep(modifiedData[levelIndex]);
        levelToAdd.dataSource = levelToAdd.dataSource.map((step: any) => {
          step.sections = [];
          return step;
        });
        setFormFlowLevels(() => [
          ...modifiedData.slice(0, levelIndex + 1),
          levelToAdd,
          ...modifiedData.slice(levelIndex + 1),
        ]);
        break;
      }
      case FORM_FLOW_MENU_ACTIONS.ADD_FILL_STEP: {
        modifiedData[levelIndex] = {
          ...modifiedData[levelIndex],
          dataSource: [...modifiedData[levelIndex].dataSource, createFlowStep(FORM_FLOW_STEP_TYPES.FILL)],
        };
        setFormFlowLevels(modifiedData);
        break;
      }
      case FORM_FLOW_MENU_ACTIONS.ADD_MODIFY_STEP: {
        modifiedData[levelIndex] = {
          ...modifiedData[levelIndex],
          dataSource: [...modifiedData[levelIndex].dataSource, createFlowStep(FORM_FLOW_STEP_TYPES.MODIFY)],
        };
        setFormFlowLevels(modifiedData);
        break;
      }
      case FORM_FLOW_MENU_ACTIONS.ADD_VERIFY_STEP: {
        modifiedData[levelIndex] = {
          ...modifiedData[levelIndex],
          dataSource: [...modifiedData[levelIndex].dataSource, createFlowStep(FORM_FLOW_STEP_TYPES.VERIFY)],
        };
        setFormFlowLevels(modifiedData);
        break;
      }
      case FORM_FLOW_MENU_ACTIONS.ADD_MOVE_BACKWARD: {
        modifiedData[levelIndex] = {
          ...modifiedData[levelIndex],
          rollBackBtnTxt: DEFAULT_ACTION_BUTTON_TEXTS.ROLL_BACK,
          rollBackBtnEditMode: false,
          allowedUsersToRollBack: [],
        };
        setFormFlowLevels(modifiedData);
        break;
      }
      case FORM_FLOW_MENU_ACTIONS.ADD_REJECT_FORM: {
        modifiedData[levelIndex] = {
          ...modifiedData[levelIndex],
          rejectBtnTxt: DEFAULT_ACTION_BUTTON_TEXTS.REJECT,
          rejectBtnEditMode: false,
          allowedUsersToReject: [],
        };
        setFormFlowLevels(modifiedData);
        break;
      }
      case FORM_FLOW_MENU_ACTIONS.REMOVE_SECTION: {
        modifiedData[levelIndex].dataSource[stepIndex].sections = modifiedData[levelIndex].dataSource[
          stepIndex
        ].sections.filter((section: any) => section._id != args.sectionId);
        setFormFlowLevels(modifiedData);
        break;
      }
      case FORM_FLOW_MENU_ACTIONS.REMOVE_USER: {
        if (actionStep) {
          modifiedData[levelIndex][actionStep.usersField] = modifiedData[levelIndex][actionStep.usersField].filter(
            (user: any) => user.id != args.userId,
          );
        } else {
          modifiedData[levelIndex].dataSource[stepIndex].users = modifiedData[levelIndex].dataSource[
            stepIndex
          ].users.filter((user: any) => user.id != args.userId);
        }
        setFormFlowLevels(modifiedData);
        break;
      }
    }
  };

  /** Removes one section from a step. */
  const removeSection = ({ sectionId, levelIndex, stepIndex }: any) =>
    handleOptionSelect({ args: { sectionId }, levelIndex, stepIndex, action: FORM_FLOW_MENU_ACTIONS.REMOVE_SECTION });

  /** Removes one user from a step, or from a level action when `actionStep` is given. */
  const removeUser = ({ userId, levelIndex, stepIndex, actionStep }: any) =>
    handleOptionSelect({
      args: { userId },
      levelIndex,
      stepIndex,
      actionStep,
      action: FORM_FLOW_MENU_ACTIONS.REMOVE_USER,
    });

  /* ---------------- saving ---------------- */

  /** Persists the edited levels onto the existing flow. */
  const handleSave = async () => {
    // `any` payload: the flow document is only loosely typed end-to-end (the server schema keeps
    // `formFlow` levels open), matching how this call has always been made.
    const data: any = {
      _id: formFlowId,
      data: {
        formFlow: toPersistableFormFlow(formFlowLevels),
        projectId,
      },
      action: "update" as const,
    };
    const res = (await formFlowController(data)) as any;
    if (res.data.status) {
      handleBackClick();
      dispatch(
        showToastNotification({
          content: t('Form flow "{{name}}" saved successfully', { name: formFlowData.name }),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    } else {
      dispatch(
        showToastNotification({
          content: untranslated(res.data.errorMessage) ?? SOMETHING_WENT_WRONG,
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };

  const handleCloseModalClick = () => {
    setOpenNewFlowSaveModal({ status: false, title: "" });
  };

  /** Saves the in-flight flow as a new reusable flow (the "Save Form Flow" dialog). */
  const handleSaveFlowClick = async () => {
    if (!resolvedSpaceId) {
      dispatch(showToastNotification({ content: t("Please select a space"), type: TOAST_TYPE_ERROR }));
      return;
    }
    const data: any = {
      data: {
        name: openNewFlowSaveModal.title,
        selectedTemplate: updatedTemplate._id,
        formCategory: updatedTemplate.resourceType,
        formFlow: toPersistableFormFlow(formFlowLevels),
        projectId,
        spaceId: resolvedSpaceId,
      },
      action: "save" as const,
    };
    const res = (await formFlowController(data)) as any;
    if (res.data.status) {
      dispatch(
        showToastNotification({
          content: t('New Form Flow "{{name}}" has been created successfully', { name: data.data.name }),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      handleCloseModalClick();
    } else {
      dispatch(
        showToastNotification({
          content: res.data.errorMessage,
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };

  /** Discards edits: reloads a saved flow, or re-seeds a new one from its template. */
  const handleCancelEditClick = async () => {
    if (formFlowId) {
      await fetchFormFlowData();
    } else {
      setInitialData();
    }
    setEditMode(false);
  };

  const openSaveFlowModal = () => setOpenNewFlowSaveModal({ status: true, title: "" });

  const handleFlowNameChange = (args: any) => {
    setOpenNewFlowSaveModal((prev) => ({
      ...prev,
      title: args.target.value,
    }));
  };

  /* ---------------- single-level edit mode ---------------- */

  // `onLevelChange` is passed as an inline arrow by its callers, so its identity changes on every
  // parent render. Reporting is driven by the edited level's content, not by the parent re-rendering,
  // so the effect triggers on the level and reaches the current callback through a ref.
  const onLevelChangeRef = useRef(onLevelChange);
  onLevelChangeRef.current = onLevelChange;

  useEffect(() => {
    if (!singleLevelEditMode || editLevelIndex == null) return;
    const level = formFlowLevels[editLevelIndex];
    if (!level) return;
    onLevelChangeRef.current(stripLevelEditFlags(level), saveBtnState);
  }, [formFlowLevels, editLevelIndex, singleLevelEditMode, saveBtnState]);

  /** The level being edited in single-level edit mode. */
  const editedLevel: FormFlowLevel | null =
    singleLevelEditMode && editLevelIndex != null ? formFlowLevels[editLevelIndex] : null;

  /* ---------------- option lists ---------------- */

  /** Actions this level actually has, in canonical order. */
  const getLevelActionSteps = (level: FormFlowLevel): FormFlowActionStep[] =>
    actionSteps.filter((step) => !!Object.hasOwn(level, step.btnTxtField));

  const levelMenuItems = (levelIndex: number) => getLevelMenuItems({ levelIndex, totalLevels: formFlowLevels.length });
  const addStepMenuItems = (levelIndex: number) =>
    getAddStepMenuItems({ levelIndex, totalLevels: formFlowLevels.length });
  const addActionMenuItems = ({ level, levelIndex }: { level: FormFlowLevel; levelIndex: number }) =>
    getAddActionMenuItems({ level, levelIndex });

  return {
    // core state
    formFlowLevels,
    setFormFlowLevels,
    formFlowData,
    updatedTemplate,
    editMode,
    setEditMode,
    saveBtnState,
    source,
    resolvedSpaceId,
    addUserQuery,

    // derived
    allSectionsUsedForFill,
    allSectionsUsedForVerify,
    getSectionsInUse,
    selectableSections,
    editedLevel,
    getLevelActionSteps,

    // option lists
    actionSteps,
    levelMenuItems,
    addStepMenuItems,
    addActionMenuItems,
    stepMenuItems: getStepMenuItems(),
    actionMenuItems: getActionMenuItems(),

    // modals
    modalState,
    setModalState,
    closeAddModal,
    openSectionsModal,
    openStepUsersModal,
    openActionUsersModal,
    allSelectedSections,
    handleSectionSelection,
    openNewFlowSaveModal,
    setOpenNewFlowSaveModal,
    openSaveFlowModal,
    handleCloseModalClick,
    handleFlowNameChange,

    // mutations
    handleOptionSelect,
    handleAddUsersClick,
    handleAddSectionsClick,
    handleAddLevelClick,
    handleConditionChange,
    handleActionBtnEdit,
    saveNewBtnName,
    cancelBtnNameChange,
    saveNewHeaderName,
    handleEditHeaderCancelClick,
    removeSection,
    removeUser,

    // persistence
    handleSave,
    handleSaveFlowClick,
    handleCancelEditClick,
    fetchFormFlowData,
    setInitialData,
  };
}

export default useFormFlowCreation;
